import type { FC } from "react";
import type { MetaAdsKPISummary } from "shared";
import { Card } from "../../components/Card";
import { ComparisonIndicator } from "./ComparisonIndicator";

const COST_METRICS: (keyof MetaAdsKPISummary)[] = [
	"spend",
	"cpc",
	"cpm",
	"costPerResult",
];

interface KPISummaryCardsProps {
	kpis: MetaAdsKPISummary;
	changes?: MetaAdsKPISummary;
}

const formatCurrency = (val: number) =>
	`$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatNumber = (val: number) =>
	val.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatPercent = (val: number) => `${val.toFixed(2)}%`;

const kpiConfigs: {
	key: keyof MetaAdsKPISummary;
	label: string;
	format: (val: number) => string;
}[] = [
	{ key: "spend", label: "Spend", format: formatCurrency },
	{ key: "impressions", label: "Impressions", format: formatNumber },
	{ key: "reach", label: "Reach", format: formatNumber },
	{ key: "clicks", label: "Clicks", format: formatNumber },
	{ key: "ctr", label: "CTR", format: formatPercent },
	{ key: "cpc", label: "CPC", format: formatCurrency },
	{ key: "cpm", label: "CPM", format: formatCurrency },
	{ key: "conversions", label: "Conversions", format: formatNumber },
	{ key: "costPerResult", label: "Cost/Result", format: formatCurrency },
];

export const KPISummaryCards: FC<KPISummaryCardsProps> = ({
	kpis,
	changes,
}) => {
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
				gap: 12,
			}}
		>
			{kpiConfigs.map((cfg) => (
				<Card key={cfg.key}>
					<Card.Body style={{ textAlign: "center", padding: "12px 8px" }}>
						<div
							style={{
								fontSize: "0.75rem",
								color: "var(--bs-secondary)",
								textTransform: "uppercase",
								marginBottom: 4,
							}}
						>
							{cfg.label}
						</div>
						<div style={{ fontSize: "1.25rem", fontWeight: 600 }}>
							{cfg.format(kpis[cfg.key])}
						</div>
						{changes && (
							<ComparisonIndicator
								change={changes[cfg.key]}
								invertColor={COST_METRICS.includes(cfg.key)}
							/>
						)}
					</Card.Body>
				</Card>
			))}
		</div>
	);
};
